import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

interface SelectContextValue {
  value: string;
  onValueChange: (value: string) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  rootRef: React.RefObject<HTMLDivElement>;
}

const SelectContext = createContext<SelectContextValue | undefined>(undefined);

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
}

export function Select({ value, onValueChange, children }: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  return (
    <SelectContext.Provider value={{ value, onValueChange, isOpen, setIsOpen, triggerRef, rootRef }}>
      <div ref={rootRef} className="relative">
        {children}
      </div>
    </SelectContext.Provider>
  );
}

interface SelectTriggerProps {
  className?: string;
  children: React.ReactNode;
}

export function SelectTrigger({ className = '', children }: SelectTriggerProps) {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('SelectTrigger must be used within Select');
  }

  const classes = `flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`;

  return (
    <button
      ref={context.triggerRef}
      className={classes}
      type="button"
      onClick={() => context.setIsOpen(!context.isOpen)}
    >
      {children}
      <ChevronDown className="h-4 w-4 opacity-50" />
    </button>
  );
}

interface SelectValueProps {
  placeholder?: string;
}

export function SelectValue({ placeholder }: SelectValueProps) {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('SelectValue must be used within Select');
  }

  return (
    <span>
      {context.value || placeholder}
    </span>
  );
}

interface SelectContentProps {
  className?: string;
  children: React.ReactNode;
}

export function SelectContent({ className = '', children }: SelectContentProps) {
  const context = useContext(SelectContext);
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, maxHeight: 240 });

  if (!context) {
    throw new Error('SelectContent must be used within Select');
  }

  const updatePosition = () => {
    const trigger = context.triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const padding = 8;
    const gap = 4;
    const preferredHeight = ref.current?.offsetHeight || 240;
    const maxAvailableBelow = window.innerHeight - rect.bottom - gap - padding;
    const maxAvailableAbove = rect.top - gap - padding;
    const openAbove = maxAvailableBelow < Math.min(preferredHeight, 160) && maxAvailableAbove > maxAvailableBelow;
    const maxHeight = Math.max(96, Math.min(240, openAbove ? maxAvailableAbove : maxAvailableBelow));

    let top = openAbove
      ? rect.top - gap - Math.min(preferredHeight, maxHeight)
      : rect.bottom + gap;
    top = Math.max(padding, Math.min(top, window.innerHeight - padding - Math.min(preferredHeight, maxHeight)));

    const width = Math.max(rect.width, 160);
    let left = rect.left;
    if (left + width > window.innerWidth - padding) {
      left = window.innerWidth - padding - width;
    }
    left = Math.max(padding, left);

    setPosition({ top, left, width, maxHeight });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        context.rootRef.current &&
        !context.rootRef.current.contains(target)
      ) {
        context.setIsOpen(false);
      }
    };

    if (context.isOpen) {
      updatePosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [context.isOpen]);

  useEffect(() => {
    if (context.isOpen) {
      updatePosition();
    }
  }, [context.isOpen, children]);

  if (!context.isOpen) {
    return null;
  }

  const classes = `fixed z-[9999] overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md ${className}`;

  return createPortal(
    <div
      ref={ref}
      className={classes}
      style={{
        top: position.top,
        left: position.left,
        minWidth: position.width,
        maxHeight: position.maxHeight,
      }}
    >
      {children}
    </div>,
    document.body
  );
}

interface SelectItemProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

export function SelectItem({ value, className = '', children }: SelectItemProps) {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('SelectItem must be used within Select');
  }

  const isSelected = context.value === value;
  const classes = `relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
    isSelected ? 'bg-accent text-accent-foreground' : ''
  } ${className}`;

  return (
    <div
      className={classes}
      onClick={() => {
        context.onValueChange(value);
        context.setIsOpen(false);
      }}
    >
      {children}
    </div>
  );
}
